// ============================================================================
// Another engagement's note / report as portable sections
// ============================================================================
// Read-only loaders shared by the "Start from" dialog (copy another
// engagement's text) and the document side panel's "Previous year" tab (last
// year's wording of the section being edited). Sections read as they print
// there — answers applied — for a generated note / report, or as typed for a
// note still on the old free-edit format.

import { fsaNotesService } from 'api/services/fsa/fsaNotesService';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import { fsaEngagementService } from 'api/services/fsa/fsaEngagementService';
import { parseCorporateFactsData } from 'hooks/api/fsa/useFsaCorporateFacts';
import { parseSectionDefinitions } from './reportSections';
import { portableFromLegacy, portableFromSectioned, type PortableSection } from './directMode';

export interface LoadedSections {
  portable: PortableSection[];
  /** False when the source was never saved (its default text). */
  saved: boolean;
}

export interface PriorSections extends LoadedSections {
  /** e.g. "ENG-2025-014". */
  engagementCode: string | null;
}

function parse(json: string | null | undefined): any {
  try { return json ? JSON.parse(json) : {}; } catch { return {}; }
}

async function factsOf(engagementId: string) {
  return parseCorporateFactsData((await fsaCorporateFactsService.get(engagementId)).data?.data);
}

/** An engagement's note with this schedule code. */
export async function loadNoteSections(engagementId: string, scheduleCode: string): Promise<LoadedSections> {
  const list = await fsaNotesService.list(engagementId);
  const item = (list.data ?? []).find(n => n.scheduleCode === scheduleCode);
  if (!item) throw new Error('That engagement has no such note (a different template?).');
  const h = (await fsaNotesService.hydrate(engagementId, item.scheduleId)).data;
  if (!h) throw new Error('The note could not be loaded.');
  const ws = parse(h.workspace?.data);
  if (h.sectionsJson) {
    return { portable: portableFromSectioned(parseSectionDefinitions(h.sectionsJson), ws, await factsOf(engagementId)), saved: !!h.workspace };
  }
  return { portable: portableFromLegacy(h.columnSpecJson, ws), saved: !!h.workspace };
}

/** An engagement's sectioned report with this report code; null when it has none in sections. */
export async function loadReportSections(engagementId: string, reportCode: string): Promise<LoadedSections | null> {
  const list = await fsaReportService.list(engagementId);
  const item = (list.data ?? []).find(r => r.reportCode === reportCode);
  if (!item) return null;
  const h = (await fsaReportService.hydrate(engagementId, item.reportId)).data;
  if (!h?.sectionsJson) return null;
  const ws = parse(h.workspace?.data);
  return { portable: portableFromSectioned(parseSectionDefinitions(h.sectionsJson), ws, await factsOf(engagementId)), saved: !!h.workspace };
}

/** The previous-year engagement linked to this one, or null. */
export async function priorEngagementOf(engagementId: string): Promise<{ id: string; code: string | null } | null> {
  const s = (await fsaEngagementService.getSettings(engagementId)).data;
  return s?.priorEngagementId ? { id: String(s.priorEngagementId), code: s.priorEngagementCode ?? null } : null;
}

/** The previous year's note / report, as saved there; null when no previous year is linked or it has none. */
export async function loadPriorSections(
  engagementId: string,
  kind: 'note' | 'report',
  code: string,
): Promise<PriorSections | null> {
  const prior = await priorEngagementOf(engagementId);
  if (!prior) return null;
  const loaded = kind === 'note'
    ? await loadNoteSections(prior.id, code).catch(() => null)
    : await loadReportSections(prior.id, code);
  return loaded ? { ...loaded, engagementCode: prior.code } : null;
}
